import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import '../../styles/global.css';
import { useTheme } from '../../context/ThemeContext';
import { ANOMALY_ALERTS, DARK } from '../../utilities/constants';
import { getCurrentFormattedDate } from '../../utilities/commonFunctions';
import PlotCard from './PlotCard';
import { Spin } from 'antd';

const CHART_COLORS = {
  overSpeeding: '#FFCF00',
  rashDriving: '#FF9D9D',
  sos: '#FF2124',
  doorOpen: '#18837E',
};

const LEGEND_ITEMS = [
  { key: 'sos', label: ANOMALY_ALERTS.SOS, color: CHART_COLORS.sos },
  {
    key: 'rash-driving',
    label: ANOMALY_ALERTS.RASH_DRIVING,
    color: CHART_COLORS.rashDriving,
  },
  {
    key: 'over-speeding',
    label: ANOMALY_ALERTS.OVER_SPEEDING,
    color: CHART_COLORS.overSpeeding,
  },
  {
    key: 'door-open',
    label: ANOMALY_ALERTS.DOOR_OPEN,
    color: CHART_COLORS.doorOpen,
  },
];

const BAR_CONFIG = {
  type: 'bar',
  width: 0.2,
  textposition: 'none',
};

const AnomalyAlertsChart = ({ anomalyData, anomalyChartLoading }) => {
  const [plotData, setPlotData] = useState([]);
  const [maxValue, setMaxValue] = useState(5);
  const { theme } = useTheme();

  const [layout, setLayout] = useState({
    barmode: 'stack',
    showlegend: false,
    plot_bgcolor: theme === DARK ? '#000000' : '#EFF1F2',
    paper_bgcolor: 'transparent',
    height: 350,
    margin: { l: 40, r: 30, t: 30, b: 30, padTop: 4 },
    font: { color: theme === DARK ? '#FFFFFF' : '#000000' },
    xaxis: {
      // tickmode: 'array',
      // gridcolor: '#3D4043',
      // categoryorder: 'array',
      fixedrange: false,
      tickangle: 0,
      ticklen: 16,
      automargin: true,
    },
    yaxis: {
      gridcolor: '#71767C',
      rangemode: 'tozero',
      fixedrange: true,
      ticklen: 16,
    },
    dragmode: false,
  });

  useEffect(() => {
    setLayout(prev => ({
      ...prev,
      font: { color: theme === DARK ? '#FFFFFF' : '#000000' },
      plot_bgcolor: theme === DARK ? '#000000' : '#EFF1F2',
    }));
  }, [theme]);

  const processTimeData = timePeriod => {
    const startTime = timePeriod.split('-')[0].split(':').slice(0, 2).join(':');
    const fullTimeRange = timePeriod
      .split('-')
      .map(value => value.split(':').slice(0, 2).join(':'))
      .join('-');
    return { startTime, fullTimeRange };
  };

  const calculateMaxValue = anomalyCounts => {
    return (
      Math.max(
        maxValue,
        ...anomalyCounts.map(
          element =>
            (element?.sos_count || 0) +
            (element?.door_open_count || 0) +
            (element?.rash_driving_count || 0) +
            (element?.overspeed_count || 0)
        )
      ) + 5
    );
  };

  const processAnomalyCounts = anomalyCounts => {
    const processedData = anomalyCounts?.reduce(
      (acc, curr, index) => {
        const { startTime, fullTimeRange } = processTimeData(curr?.time_period);

        const uniqueTimeKey = index;
        acc.tickLabels.push(startTime); // <- this is just the label
        acc.tickValues.push(uniqueTimeKey); // <- this is the internal x
        acc.date.push(curr?.date);
        acc.hovertemplate.push(fullTimeRange);
        acc.overSpeeding.push(curr?.overspeed_count || 0);
        acc.rashDriving.push(curr?.rash_driving_count || 0);
        acc.sos.push(curr?.sos_count || 0);
        acc.doorOpen.push(curr?.door_open_count || 0);

        return acc;
      },
      {
        overSpeeding: [],
        rashDriving: [],
        sos: [],
        doorOpen: [],
        hovertemplate: [],
        tickLabels: [],
        tickValues: [],
        date: [],
      }
    );

    return processedData;
  };

  const createPlotData = processedData => {
    const seriesConfig = [
      { key: 'overSpeeding', name: 'Over Speeding' },
      { key: 'rashDriving', name: 'Rash Driving' },
      { key: 'sos', name: 'SOS' },
      { key: 'doorOpen', name: 'Door Open' },
    ];

    return seriesConfig.map(({ key, name }) => ({
      x: processedData.tickValues,
      y: processedData[key],
      name,
      ...BAR_CONFIG,
      marker: { color: CHART_COLORS[key] },
      text: processedData.hovertemplate,
      hovertemplate: `Time Period: %{text}<br>Count: %{y}<extra></extra>`,
    }));
  };

  const updateChart = useCallback(
    anomalyCounts => {
      if (!anomalyCounts) return;

      const newMaxValue = calculateMaxValue(anomalyCounts);
      const processedData = processAnomalyCounts(anomalyCounts);
      const newPlotData = createPlotData(processedData);

      // Find indices where tickLabel is '00:00'
      const zeroHourIndices = processedData.tickLabels
        .map((label, i) => (label === '00:00' ? i : -1))
        .filter(index => index !== -1);

      // Generate shapes for each '00:00'
      const shapes = zeroHourIndices.map(index => ({
        type: 'line',
        x0: index,
        x1: index,
        y0: 0,
        y1: 1.1,
        yref: 'paper',
        line: {
          color: '#E552DA',
          width: 1,
          dash: 'solid',
        },
      }));

      // Generate annotations for each '00:00'
      const annotations = zeroHourIndices.map(index => ({
        x: index,
        y: 1.1,
        yref: 'paper',
        xref: 'x',
        xanchor: 'left',
        showarrow: false,
        text: getCurrentFormattedDate(),
        font: {
          color: '#000000',
          size: 12,
          weight: 400,
        },
        bgcolor: '#E552DA',
        bordercolor: '#E552DA',
        borderwidth: 1,
        borderpad: 4,
        xshift: 4,
      }));

      setMaxValue(newMaxValue);
      setPlotData(newPlotData);
      setLayout(prev => ({
        ...prev,
        xaxis: {
          ...prev.xaxis,
          tickvals: processedData.tickValues,
          ticktext: processedData.tickLabels,
        },
        yaxis: { ...prev.yaxis, range: [0, newMaxValue] },
        shapes,
        annotations,
      }));
    },
    [maxValue]
  );

  useEffect(() => {
    if (anomalyData) {
      updateChart(anomalyData);
    }
  }, [anomalyData]);

  return (
    <Spin
      spinning={anomalyChartLoading}
      size="large"
      className="overview-chart-spinner"
    >
      <PlotCard
        title="Anomaly Alerts"
        plotData={plotData}
        layout={layout}
        legendItems={LEGEND_ITEMS}
      />
    </Spin>
  );
};

AnomalyAlertsChart.propTypes = {
  anomalyData: PropTypes.array,
  anomalyChartLoading: PropTypes.bool,
};

export default AnomalyAlertsChart;
